import {
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  getFullName,
  TableActionsDropdown,
  AbbreviateTableColumn,
  getTranslatedMTTRoles,
  getTranslatedMTTFunctions,
  Button,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import {
  createFileRoute,
  useRouter,
  useNavigate,
  useSearch,
} from "@tanstack/react-router"
import { useState } from "react"
import {
  TOPTAAL_USER_STORAGE_KEY,
  useToptaalUserFilters,
} from "@/hooks/filters/useToptaalUserFilters"
import { User } from "@/api/types/user"
import { NewFreelancerModal } from "@/components/modals/user/NewFreelancerModal"
import ScreenWrapper from "@/components/ScreenWrapper"
import { TABLE_PAGE_SIZE } from "toptaal-shared"
import EditRoleModal from "@/components/modals/user/EditRoleModal"
import { checkPermissions } from "@/utils/checkPermissions"
import EditProfileModal from "@/components/modals/user/EditProfileModal"
import { UserWithProfile } from "@/api/types/toptaalUser"
import EditTeacherProfileModal from "@/components/modals/user/EditTeacherProfileModal"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  useExportMutation,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { userQueryKeys } from "@/api/query-keys/users"
import DocumentModal from "@/components/modals/DocumentModal"
import { can } from "@/utils/can"
import { freelanceStateMap } from "@/api/types/user"
import { useQuery } from "@tanstack/react-query"
import { getTopTaalUserQuery } from "@/api/queries/toptaalUsers/useTopTaalUser"
import { ArchiveToptaalUserModal } from "@/components/modals/user/ArchiveToptaalUserModal"
import { UnarchiveToptaalUserModal } from "@/components/modals/user/UnarchiveToptaalUserModal"
import { z } from "zod"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"
import { isMissingTeacherInfoUtil } from "@/utils/isMissingTeacherInfoUtil"

export const Route = createFileRoute("/beheer/medewerkers/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "employees"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

const DEFAULT_COLUMN_VISIBILITY = {
  isTeacher: false,
  isFreelance: false,
  freelanceState: false,
  postalCode: false,
  city: false,
  missingDocuments: false,
}

const ALL_COLUMN_IDS = [
  "userId",
  "firstName",
  "email",
  "functions",
  "roles",
  "phoneNumber",
  "isTeacher",
  "isFreelance",
  "freelanceState",
  "postalCode",
  "city",
  "missingDocuments",
]

function RouteComponent() {
  const [activeRow, setActiveRow] = useState<UserWithProfile | null>(null)
  const [editRoleOpen, setEditRoleOpen] = useState(false)
  const [editProfileIsOpen, setEditProfileIsOpen] = useState(false)
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false)
  const [editQualificationIsOpen, setEditQualificationIsOpen] = useState(false)
  const [isNewFreelancerModalOpen, setIsNewFreelancerModal] = useState(false)
  const [archiveUserIsOpen, setArchiveUserIsOpen] = useState(false)
  const [unarchiveUserIsOpen, setUnarchiveUserIsOpen] = useState(false)

  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/medewerkers/" })
  const navigate = useNavigate({ from: "/beheer/medewerkers/" })
  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useToptaalUserFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    queryKeyMerged,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...userQueryKeys.all, { archived }, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: TOPTAAL_USER_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const handleOpenNewFreelancerModal = () => setIsNewFreelancerModal(true)

  const handleNavigateUserDetail = (userId: User["userId"]) => {
    router.navigate({
      to: "/beheer/medewerkers/$userId/persoonlijke_gegevens",
      params: { userId: String(userId) },
      search: { from: router.state.location.href },
    })
  }

  const getFreelanceActions = (user: UserWithProfile) => [
    ...(can("update", "employees")
      ? [
          {
            label: "Rollen beheren",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setEditRoleOpen(true)
            },
          },
        ]
      : []),
    {
      label: "Persoonlijke gegevens wijzigen",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation()
        setActiveRow(user)
        setEditProfileIsOpen(true)
      },
    },
    ...(user.isTeacher
      ? [
          {
            label: "Lesbevoegdheid wijzigen",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setEditQualificationIsOpen(true)
            },
          },
        ]
      : []),
    {
      label: "Document uploaden",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation()
        setActiveRow(user)
        setUploadDocumentOpen(true)
      },
    },
    ...(archived
      ? [
          {
            label: "Medewerker herstellen",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setUnarchiveUserIsOpen(true)
            },
          },
        ]
      : [
          {
            label: "Medewerker archiveren",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()

              setActiveRow(user)
              setArchiveUserIsOpen(true)
            },
          },
        ]),
  ]

  const getEmployeeActions = (user: UserWithProfile) => [
    ...(can("update", "employees")
      ? [
          {
            label: "Rollen beheren",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setEditRoleOpen(true)
            },
          },
        ]
      : []),
    ...(user.isTeacher
      ? [
          {
            label: "Lesbevoegdheid wijzigen",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setEditQualificationIsOpen(true)
            },
          },
        ]
      : []),
    {
      label: "Persoonlijke gegevens wijzigen",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation()
        setActiveRow(user)
        setEditProfileIsOpen(true)
      },
    },
  ]

  const columns: ColumnDef<UserWithProfile>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "userId", name: "ID" }),
    createCustomColumn({
      id: "firstName",
      name: "Naam",
      cell: ({ row }) =>
        getFullName({
          firstName: row.original.firstName,
          namePrefix: row.original.namePrefix,
          lastName: row.original.lastName,
        }),
      accessorKey: "firstName",
    }),
    createSortableColumn({ accessorKey: "email", name: "E-mail" }),
    createCustomColumn({
      id: "functions",
      name: "Functie",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={getTranslatedMTTFunctions(row.original.functions)
            .split(", ")
            .map((func) => ({ name: func }))}
        />
      ),
      accessorKey: "functions",
    }),
    createCustomColumn({
      id: "roles",
      name: "Rollen",
      cell: ({ row }) => (
        <AbbreviateTableColumn
          entities={getTranslatedMTTRoles(row.original.roles)
            .split(", ")
            .map((role) => ({ name: role }))}
        />
      ),
      accessorKey: "roles",
    }),
    createSortableColumn({
      accessorKey: "phoneNumber",
      name: "Telefoonnummer",
    }),
    createCustomColumn({
      id: "isTeacher",
      name: "Docent",
      cell: ({ row }) => (row.original.isTeacher ? "Ja" : "Nee"),
      accessorKey: "isTeacher",
    }),
    createCustomColumn({
      id: "isFreelance",
      name: "ZZP",
      cell: ({ row }) => (row.original.isFreelance ? "Ja" : "Nee"),
      accessorKey: "isFreelance",
    }),
    createCustomColumn({
      id: "freelanceState",
      name: "ZZP status",
      cell: ({ row }) =>
        row.original.freelanceState
          ? freelanceStateMap[row.original.freelanceState]
          : "-",
      accessorKey: "freelanceState",
      enableSorting: false,
    }),
    createSortableColumn({ accessorKey: "postalCode", name: "Postcode" }),
    createSortableColumn({ accessorKey: "city", name: "Plaats" }),
    createCustomColumn({
      id: "missingDocuments",
      name: "Ontbrekende documenten",
      cell: ({ row }) => {
        const user = row.original

        const hasMissingDocuments = isMissingTeacherInfoUtil(user)

        return hasMissingDocuments ? "Ja" : "Nee"
      },
      accessorKey: "documents",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "actions",
      name: "",
      cell: ({ row }) => {
        const actions = row.original.isFreelance
          ? getFreelanceActions(row.original)
          : getEmployeeActions(row.original)

        return <TableActionsDropdown row={row} actions={actions} />
      },
      enableSorting: false,
      enableHiding: false,
    }),
  ]

  const { data: detailedUserResponse } = useQuery({
    ...getTopTaalUserQuery(activeRow?.userId),
    enabled: !!activeRow?.userId && (editProfileIsOpen || editRoleOpen),
    placeholderData: activeRow ? { data: activeRow } : undefined,
  })

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  const activeUser = detailedUserResponse?.data ?? activeRow ?? null

  const { mutate: exportUsers } = useExportMutation({
    exportApiPrefix: "toptaal_users",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title={archived ? "Medewerkers archief" : "Medewerkers"}
        data={data?.data.users || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        onRowClick={handleNavigateUserDetail}
        columns={columns}
        rowIdentifier="userId"
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <TableHeaderActions
            exportAction={{
              onClick: exportUsers,
            }}
            createAction={{
              label: "Nieuwe zzp'er",
              onClick: handleOpenNewFreelancerModal,
              entity: "employees",
              permissions: "create",
            }}
          />
        }
      />

      {activeRow && (
        <EditTeacherProfileModal
          editTeacherProfileIsOpen={editQualificationIsOpen}
          setEditTeacherProfileIsOpen={setEditQualificationIsOpen}
          activeRow={activeRow}
          key={`edit-qualification-modal-${activeRow.userId}`}
          queryKey={queryKeyMerged}
        />
      )}

      {activeRow && (
        <DocumentModal
          open={uploadDocumentOpen}
          setOpen={setUploadDocumentOpen}
          queryKey={queryKeyMerged}
          key={
            "upload-document-modal-" + activeRow.userId ||
            "upload-document-modal"
          }
          id={activeRow.userId}
          entity="TOPTAAL_USER"
          title={`Document uploaden voor ${getFullName({ firstName: activeRow.firstName, lastName: activeRow.lastName })}`}
        />
      )}

      <NewFreelancerModal
        isNewFreelancerModalOpen={isNewFreelancerModalOpen}
        setIsNewFreelancerModal={setIsNewFreelancerModal}
        queryKey={queryKeyMerged}
      />

      {activeRow && (
        <EditProfileModal
          editProfileIsOpen={editProfileIsOpen}
          setEditProfileIsOpen={setEditProfileIsOpen}
          activeRow={activeUser}
          queryKey={queryKeyMerged}
        />
      )}

      {activeRow && (
        <EditRoleModal
          editRoleOpen={editRoleOpen}
          setEditRoleOpen={setEditRoleOpen}
          activeRow={activeUser as User}
          queryKey={queryKeyMerged}
          key={`edit-role-modal-${activeUser?.userId}`}
        />
      )}

      {activeUser && (
        <ArchiveToptaalUserModal
          isOpen={archiveUserIsOpen}
          setIsOpen={setArchiveUserIsOpen}
          user={activeUser}
        />
      )}

      {activeUser && (
        <UnarchiveToptaalUserModal
          isOpen={unarchiveUserIsOpen}
          setIsOpen={setUnarchiveUserIsOpen}
          user={activeUser}
        />
      )}
    </ScreenWrapper>
  )
}
